import type { DigestProfileStat } from '../components/shared/SharedDigestFilterBar';

/** Fields used to associate library/audio items with a digest profile. */
export interface DigestMatchableItem {
  profile_id?: string | null;
  specialty_id?: string | null;
  subspecialty_id?: string | null;
  specialty_name?: string | null;
  folder?: string | null;
}

/** Match an item to a digest profile (UUID, specialty, subspecialty, labels, folder). */
export function itemMatchesDigestProfile(
  item: DigestMatchableItem,
  profile: DigestProfileStat
): boolean {
  if (item.profile_id && item.profile_id === profile.profile_id) return true;

  if (item.specialty_id && item.specialty_id === profile.specialty_id) return true;
  if (item.subspecialty_id && item.subspecialty_id === profile.subspecialty_id) return true;

  const profileSlug = profile.name?.toLowerCase() ?? '';
  if (item.specialty_id && profileSlug.includes(String(item.specialty_id).toLowerCase())) return true;
  if (item.subspecialty_id && profileSlug.includes(String(item.subspecialty_id).toLowerCase())) return true;

  const specialtyName = item.specialty_name?.toLowerCase().trim();
  if (specialtyName) {
    const labels = [profile.specialty_label, profile.subspecialty_label]
      .filter(Boolean)
      .map((l) => l.toLowerCase());
    if (labels.some((l) => l === specialtyName || specialtyName.includes(l) || l.includes(specialtyName))) {
      return true;
    }
  }

  const folder = item.folder?.toLowerCase().trim();
  if (folder && folder !== 'ungrouped' && folder !== 'inbox') {
    const folderKeys = [
      profile.specialty_label,
      profile.subspecialty_label,
      profile.name?.replace(/_/g, ' '),
    ]
      .filter(Boolean)
      .map((s) => s!.toLowerCase());
    if (folderKeys.some((key) => folder.includes(key) || key.includes(folder))) {
      return true;
    }
  }

  return false;
}

/** Merge audio/API item fields with LitHub library metadata (by PMID). */
export function resolveDigestMatchFields(
  item: DigestMatchableItem & { pmid?: string | null },
  libraryByPmid?: Map<string, DigestMatchableItem>
): DigestMatchableItem {
  const lib = item.pmid ? libraryByPmid?.get(String(item.pmid)) : undefined;
  if (!lib) return item;
  return {
    profile_id: item.profile_id ?? lib.profile_id,
    specialty_id: item.specialty_id ?? lib.specialty_id,
    subspecialty_id: item.subspecialty_id ?? lib.subspecialty_id,
    specialty_name: item.specialty_name ?? lib.specialty_name,
    folder: item.folder ?? lib.folder,
  };
}

/** True when item belongs to digest profile, using optional library lookup by PMID. */
export function itemMatchesDigestProfileWithLibrary(
  item: DigestMatchableItem & { pmid?: string | null },
  profile: DigestProfileStat,
  libraryByPmid?: Map<string, DigestMatchableItem>
): boolean {
  return itemMatchesDigestProfile(resolveDigestMatchFields(item, libraryByPmid), profile);
}
